import { useState, useEffect, useCallback } from "react";
import { LogOut, Loader2 } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Logo } from "@/components/landing/Navbar";
import { EntryTable } from "@/components/admin/EntryTable";
import { Analytics } from "@/components/admin/Analytics";
import { api, errorMessage } from "@/lib/api";

const KEY = "tn_admin_pw";
const WAIT_COLS = [["position", "#"], ["created_at", "Date"], ["email", "Email"], ["mobile", "Mobile"], ["interest", "Interest"], ["referrals", "Refs"], ["source", "Form"]];
const RA_COLS = [["created_at", "Date"], ["name", "Name"], ["sebi_reg", "SEBI RA reg."], ["email", "Email"]];

const Login = ({ onLogin }) => {
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try { await api.post("/admin/login", { password: pw }); onLogin(pw); }
    catch (e2) { setErr(errorMessage(e2)); }
    setBusy(false);
  };
  return (
    <form onSubmit={submit} className="tn-card p-8 w-full max-w-sm space-y-4" data-testid="admin-login-form">
      <Logo />
      <h1 className="font-display text-xl font-semibold pt-2">Admin access</h1>
      <input type="password" className="tn-input" placeholder="Password" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="admin-password-input" />
      {err && <p className="text-sm text-[var(--tn-red)]" data-testid="admin-login-error">{err}</p>}
      <button className="tn-btn w-full h-11" disabled={busy || !pw} data-testid="admin-login-btn">{busy && <Loader2 size={16} className="animate-spin" />} Sign in</button>
    </form>
  );
};

export default function Admin() {
  const [pw, setPw] = useState(() => sessionStorage.getItem(KEY) || "");
  const [data, setData] = useState({ waitlist: [], ra: [] });

  const logout = () => { sessionStorage.removeItem(KEY); setPw(""); };
  const headers = { "X-Admin-Password": pw };

  const load = useCallback(async () => {
    const h = { "X-Admin-Password": pw };
    try {
      const [w, r] = await Promise.all([api.get("/admin/waitlist", { headers: h }), api.get("/admin/ra-applications", { headers: h })]);
      setData({ waitlist: w.data, ra: r.data });
    } catch { logout(); }
  }, [pw]);

  useEffect(() => { if (pw) load(); }, [pw, load]);

  const login = (p) => { sessionStorage.setItem(KEY, p); setPw(p); };

  if (!pw) return <div className="min-h-screen flex items-center justify-center px-5 tn-grid-bg"><Login onLogin={login} /></div>;

  return (
    <div className="min-h-screen" data-testid="admin-dashboard">
      <header className="border-b border-[var(--tn-line)] h-16 px-5 md:px-8 flex items-center justify-between">
        <Logo />
        <button className="tn-btn-ghost h-9" onClick={logout} data-testid="admin-logout-btn"><LogOut size={15} /> Log out</button>
      </header>
      <main className="max-w-[1152px] mx-auto px-5 md:px-8 py-10">
        <h1 className="font-display text-3xl font-semibold">Sign-ups</h1>
        <Tabs defaultValue="waitlist" className="mt-6">
          <TabsList className="bg-[var(--tn-card)] border border-[var(--tn-line)]">
            <TabsTrigger value="waitlist" data-testid="admin-tab-waitlist">Waitlist ({data.waitlist.length})</TabsTrigger>
            <TabsTrigger value="ra" data-testid="admin-tab-ra">RA applications ({data.ra.length})</TabsTrigger>
            <TabsTrigger value="analytics" data-testid="admin-tab-analytics">Analytics</TabsTrigger>
          </TabsList>
          <TabsContent value="waitlist" className="mt-6">
            <EntryTable rows={data.waitlist} cols={WAIT_COLS} name="waitlist" kind="waitlist" headers={headers} onRows={(waitlist) => setData((d) => ({ ...d, waitlist }))} />
          </TabsContent>
          <TabsContent value="ra" className="mt-6">
            <EntryTable rows={data.ra} cols={RA_COLS} name="ra" kind="ra-applications" headers={headers} onRows={(ra) => setData((d) => ({ ...d, ra }))} />
          </TabsContent>
          <TabsContent value="analytics" className="mt-6"><Analytics waitlist={data.waitlist} ra={data.ra} /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
